import { khuyenMaiMoi, type KhuyenMaiMau } from './khuyenMaiMoi';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, NumberInput, Select, SelectItem, Tag, TextArea, TextInput, Toggle } from '@carbon/react';
import { api, ApiError } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { MoneyInput } from '../components/MoneyInput';
import { ImageField } from '../components/ImageField';

// Deposit promotions shown in the game (backend/lib/khuyen_mai.js). A NAP promotion pays a bonus on
// the deposit that the player registered for; withdrawals stay blocked until the wagering is met.
export type Promo = {
  ma: string; ten: string; moTa?: string; nhan?: string; dieuKhoan?: string; loai: 'NAP' | 'CSKH' | 'ANH'; bat: boolean; tu?: string; den?: string;
  phanTram?: number; toiDa?: number; napToiThieu?: number; lanNap?: 0 | 1; kenh?: 'bank' | 'usdt' | 'tat_ca'; vongCuoc?: number; gocCuoc?: 'NAP' | 'NAP_THUONG';
  chiTiet?: { nhan: string; giaTri: string }[]; lienHe?: string; anh?: string;
};
type Doc = { giayChot: number; ds: Promo[] };
type Data = { noiDung: Doc; mauMoi: KhuyenMaiMau | null; meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string }[] };

const num = (v: string) => Math.trunc(Number(v.replace(/\D/g, '')) || 0);

// a type change drops the fields the new type refuses (backend/lib/khuyen_mai.js loiCauHinh)
function doiLoai(k: Promo, loai: Promo['loai']): Promo {
  const next: Promo = { ...k, loai };
  if (loai !== 'ANH') delete next.anh;
  else for (const f of ['phanTram', 'toiDa', 'napToiThieu', 'lanNap', 'kenh', 'vongCuoc', 'gocCuoc'] as const) delete next[f];
  return next;
}

function PromoEditor({ k, onChange, onRemove }: { k: Promo; onChange: (k: Promo) => void; onRemove: () => void }) {
  const set = (p: Partial<Promo>) => onChange({ ...k, ...p });
  const nap = k.loai === 'NAP';
  const anh = k.loai === 'ANH';
  return (
    <div className="section">
      <div className="row" style={{ alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>{k.ten || k.ma}</h3>
        <Tag type={k.bat ? 'green' : 'gray'}>{k.bat ? 'Đang bật' : 'Tắt'}</Tag>
        <Tag type={nap ? 'purple' : anh ? 'blue' : 'teal'}>{nap ? 'Thưởng nạp' : anh ? 'Sự kiện ảnh' : 'Ưu đãi CSKH'}</Tag>
        <span style={{ flex: 1 }} />
        <Toggle id={'bat-' + k.ma} labelText="" labelA="Tắt" labelB="Bật" size="sm" toggled={k.bat} onToggle={(v) => set({ bat: v })} />
        <Button kind="ghost" size="sm" onClick={onRemove}>Xóa</Button>
      </div>
      <div className="row row--top">
        <TextInput id={'ma-' + k.ma} labelText="Mã (A-Z, 0-9, _)" value={k.ma} onChange={(e) => set({ ma: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
        <TextInput id={'ten-' + k.ma} labelText="Tên hiện cho khách" value={k.ten} onChange={(e) => set({ ten: e.target.value })} />
        <TextInput id={'nhan-' + k.ma} labelText="Nhãn nhỏ" value={k.nhan ?? ''} onChange={(e) => set({ nhan: e.target.value })} />
        <Select id={'loai-' + k.ma} labelText="Loại" value={k.loai} onChange={(e) => onChange(doiLoai(k, e.target.value as Promo['loai']))}>
          <SelectItem value="NAP" text="Thưởng nạp (tự động)" />
          <SelectItem value="CSKH" text="Ưu đãi CSKH (thông tin)" />
          <SelectItem value="ANH" text="Sự kiện ảnh (chỉ hiện ảnh)" />
        </Select>
      </div>
      {nap ? (
        <div className="row row--top">
          <TextInput id={'pt-' + k.ma} labelText="Thưởng % tiền nạp" inputMode="numeric" value={String(k.phanTram ?? '')} onChange={(e) => set({ phanTram: num(e.target.value) })} />
          <MoneyInput id={'td-' + k.ma} labelText="Thưởng tối đa" value={k.toiDa ?? 0} onChange={(v) => set({ toiDa: Number(v) || 0 })} />
          <MoneyInput id={'nt-' + k.ma} labelText="Nạp tối thiểu" value={k.napToiThieu ?? 0} onChange={(v) => set({ napToiThieu: Number(v) || 0 })} />
          <TextInput id={'vc-' + k.ma} labelText="Vòng cược" inputMode="numeric" value={String(k.vongCuoc ?? '')} onChange={(e) => set({ vongCuoc: num(e.target.value) })} />
        </div>
      ) : null}
      {nap ? (
        <div className="row row--top">
          <Select id={'kenh-' + k.ma} labelText="Kênh nạp" value={k.kenh ?? 'tat_ca'} onChange={(e) => set({ kenh: e.target.value as Promo['kenh'] })}>
            <SelectItem value="tat_ca" text="Ngân hàng và USDT" />
            <SelectItem value="bank" text="Chỉ ngân hàng" />
            <SelectItem value="usdt" text="Chỉ USDT" />
          </Select>
          <Select id={'ln-' + k.ma} labelText="Áp dụng" value={String(k.lanNap ?? 0)} onChange={(e) => set({ lanNap: Number(e.target.value) as 0 | 1 })}>
            <SelectItem value="1" text="Chỉ lần nạp đầu tiên" />
            <SelectItem value="0" text="Lần nạp kế tiếp sau khi đăng ký" />
          </Select>
          <Select id={'gc-' + k.ma} labelText="Vòng cược tính trên" value={k.gocCuoc ?? 'NAP_THUONG'} onChange={(e) => set({ gocCuoc: e.target.value as Promo['gocCuoc'] })}>
            <SelectItem value="NAP_THUONG" text="Tiền nạp + thưởng" />
            <SelectItem value="NAP" text="Tiền nạp" />
          </Select>
        </div>
      ) : anh ? (
        <ImageField id={'anh-' + k.ma} value={k.anh} onChange={(a) => set({ anh: a })} />
      ) : (
        <TextInput id={'lh-' + k.ma} labelText="Link liên hệ CSKH (để trống = live chat)" value={k.lienHe ?? ''} onChange={(e) => set({ lienHe: e.target.value || undefined })} />
      )}
      <div className="row row--top">
        <TextInput id={'tu-' + k.ma} labelText="Bắt đầu (ISO, để trống = ngay)" value={k.tu ?? ''} onChange={(e) => set({ tu: e.target.value || undefined })} />
        <TextInput id={'den-' + k.ma} labelText="Kết thúc (ISO, để trống = không hết hạn)" value={k.den ?? ''} onChange={(e) => set({ den: e.target.value || undefined })} />
      </div>
      <TextArea id={'mt-' + k.ma} labelText="Mô tả ngắn" rows={2} value={k.moTa ?? ''} onChange={(e) => set({ moTa: e.target.value })} />
      <TextArea id={'dk-' + k.ma} labelText="Điều khoản (mỗi dòng một ý)" rows={4} value={k.dieuKhoan ?? ''} onChange={(e) => set({ dieuKhoan: e.target.value })} />
    </div>
  );
}

export function KhuyenMaiPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['khuyen-mai'], queryFn: () => api.get<Data>('/api/admin/khuyen-mai') });
  // the draft keeps the version it was built on, so a stale save is refused (409 STALE)
  const [draft, setDraft] = useState<{ doc: Doc; version: number } | null>(null);
  const live: Doc = draft?.doc ?? q.data?.noiDung ?? { giayChot: 60, ds: [] };
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning'; title: string; subtitle: string } | null>(null);
  const reload = (title: string, subtitle: string) => {
    setDraft(null); setOpen(false); setNotice({ kind: 'warning', title, subtitle }); qc.invalidateQueries({ queryKey: ['khuyen-mai'] });
  };
  const set = (doc: Doc) => setDraft({ doc, version: draft?.version ?? q.data?.meta.version ?? 0 });
  const save = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/khuyen-mai', { noiDung: live, expect: draft?.version ?? q.data?.meta.version ?? 0 }),
    onSuccess: (r) => {
      const c = r.command;
      if (c.status === 'FAILED' && c.lastError === 'PHIEN_BAN_CU') {
        reload('Khuyến mãi vừa được người khác lưu', 'Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại.');
        return;
      }
      setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['khuyen-mai'] });
      setNotice(c.status === 'SUCCEEDED'
        ? { kind: 'success', title: 'Hệ A đã nhận khuyến mãi mới', subtitle: 'Người chơi thấy ngay khi mở trang khuyến mãi.' }
        : { kind: 'warning', title: 'Lệnh chưa xong', subtitle: (c.lastError && HE_A_ERRORS[c.lastError]) || c.lastError || c.status });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && e.code === 'STALE') {
        reload('Khuyến mãi vừa được người khác lưu', 'Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại.');
      }
    },
  });

  return (
    <div className="page">
      <PageHeader title="Khuyến mãi nạp" lead="Danh sách khuyến mãi trong game. Khách phải liên kết ngân hàng mới nạp tiền và đăng ký khuyến mãi được; thưởng cộng khi duyệt lệnh nạp; rút bị chặn tới khi đủ vòng cược." />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <>
          <p className="muted">Phiên bản {q.data?.meta.version} ({q.data?.meta.source === 'cms' ? 'CMS' : 'mặc định'}){q.data?.meta.at ? <> · <When v={q.data.meta.at} /></> : null}</p>
          {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} subtitle={notice.subtitle} onClose={() => setNotice(null)} /> : null}
          {q.data?.pending.length ? <p>Có lệnh đang chờ: {q.data.pending.map((p) => <CommandStatus key={p.id} s={p.status} />)}</p> : null}
          <div className="stack">
            <div style={{ maxWidth: 360 }}>
              <NumberInput id="giay-chot" label="Chờ sau cược cuối (giây)" helperText="Đủ vòng cược rồi vẫn chờ chừng này giây sau cược cuối (cược đó còn có thể được hoàn) mới cho rút." min={0} max={3600}
                value={live.giayChot} onChange={(_e, { value }) => set({ ...live, giayChot: Math.max(0, Math.min(3600, Number(value) || 0)) })} />
            </div>
            {live.ds.map((k, i) => (
              <PromoEditor key={i} k={k}
                onChange={(n) => { const ds = [...live.ds]; ds[i] = n; set({ ...live, ds }); }}
                onRemove={() => set({ ...live, ds: live.ds.filter((_, j) => j !== i) })} />
            ))}
            <div className="row">
              <Button kind="tertiary" size="sm" disabled={!q.data?.mauMoi} onClick={() => q.data?.mauMoi && set({ ...live, ds: [...live.ds, khuyenMaiMoi(q.data.mauMoi, live.ds.length + 1)] })}>Thêm khuyến mãi nạp</Button>
              <Button kind="ghost" size="sm" onClick={() => set({ ...live, ds: [...live.ds, { ma: 'CSKH_' + (live.ds.length + 1), ten: 'Ưu đãi CSKH', loai: 'CSKH', bat: false, moTa: '' }] })}>Thêm ưu đãi CSKH</Button>
              <Button kind="ghost" size="sm" onClick={() => set({ ...live, ds: [{ ma: 'SUKIEN_' + (live.ds.length + 1), ten: 'Sự kiện mới', loai: 'ANH', bat: false }, ...live.ds] })}>Thêm sự kiện ảnh</Button>
            </div>
          </div>
          <div className="row" style={{ marginTop: 16 }}>
            <Button disabled={!draft} onClick={() => { setNotice(null); save.reset(); setOpen(true); }}>Lưu lên hệ A</Button>
            {draft ? <Button kind="ghost" onClick={() => setDraft(null)}>Bỏ thay đổi</Button> : null}
          </div>
        </>
      )}
      <Modal open={open} modalHeading="Lưu khuyến mãi" primaryButtonText="Gửi" secondaryButtonText="Hủy"
        primaryButtonDisabled={save.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => save.mutate()}>
        <div className="stack">
          <p>Khách đã đăng ký giữ điều kiện lúc đăng ký (%, tối đa, vòng cược). Tắt hoặc xóa một khuyến mãi thì lượt đăng ký chưa nạp của nó bị hủy khi khách nạp.</p>
          <InlineError error={save.error} />
        </div>
      </Modal>
    </div>
  );
}
